/**
 * Shared sentence utilities for flashcard blanking and related text helpers.
 *
 * This module centralizes the logic for splitting a sentence into
 * [before, "__BLANK__", after] while handling diacritics and Mandarin variants.
 *
 * Exports:
 * - BLANK_TOKEN
 * - stripDiacritics(s)
 * - stripLeadingPunctuation(s)
 * - splitSentence(flashcard)
 */

/** Token inserted to represent the blank area in the sentence parts result. */
export const BLANK_TOKEN = "__BLANK__";

/**
 * Remove Unicode combining diacritics from a string.
 * Example: "rápido" -> "rapido"
 * @param {unknown} s
 * @returns {string}
 */
export function stripDiacritics(s) {
  return (typeof s === "string" ? s : String(s ?? ""))
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function normalizeApostrophes(s) {
  return String(s ?? "").replace(/[’‘]/g, "'");
}

export function normalizeForSearch(s) {
  return stripDiacritics(normalizeApostrophes(String(s))).toLowerCase();
}

/**
 * Removes leading whitespace and opening punctuation characters commonly
 * found at the start of sentences or quoted text.
 * @param {unknown} s
 * @returns {string}
 */
export function stripLeadingPunctuation(s) {
  const str = typeof s === "string" ? s : String(s ?? "");
  const openingChars = new Set([
    '"',
    "“",
    "”",
    "‘",
    "’",
    "«",
    "»",
    "¡",
    "¿",
    "(",
    "[",
    "{",
  ]);
  let i = 0;
  while (i < str.length && (str[i] === " " || openingChars.has(str[i]))) {
    i++;
  }
  return str.slice(i);
}

/**
 * Split a sentence into [before, BLANK_TOKEN, after] by locating the target word.
 * - Ignores diacritics and case.
 * - Uses whole-word boundaries for Latin scripts (letters/digits).
 * - For Mandarin ("zh"), attempts both simplified and traditional forms:
 *   - language_word
 *   - language_word_traditional (if present)
 *
 * If no match is found, returns [sentence] (no blank).
 *
 * @param {object} flashcard
 * @param {string} flashcard.language_sentence - The full sentence.
 * @param {string} flashcard.language_word - The target word (usually simplified for Mandarin).
 * @param {string} [flashcard.language] - Language code (e.g., "es", "fr", "zh").
 * @param {string} [flashcard.language_word_traditional] - Traditional form for Mandarin, if available.
 * @returns {string[]} An array of parts, either [before, BLANK_TOKEN, after] or [sentence] on no match.
 */
export function splitSentence(flashcard) {
  // Guard rails
  if (!flashcard?.language_sentence) {
    return [];
  }
  if (!flashcard?.language_word) {
    return [String(flashcard.language_sentence)];
  }

  const sentence = String(flashcard.language_sentence);

  // Build candidate variants for the target word
  const language = String(flashcard.language || "").toLowerCase();
  const wordVariants = [];

  // Always include the primary word
  wordVariants.push(String(flashcard.language_word));

  // For Mandarin, also try the traditional form if present
  if (language === "zh" && flashcard.language_word_traditional) {
    wordVariants.push(String(flashcard.language_word_traditional));
  }

  // Deduplicate and filter empties
  const uniqueVariants = Array.from(
    new Set(wordVariants.map((w) => (w ?? "").trim()))
  ).filter(Boolean);

  // Build a normalized string and a map from normalized indices -> original indices
  const buildNormAndMap = (s) => {
    let norm = "";
    const map = [];
    for (let i = 0; i < s.length; i++) {
      const ch2 = normalizeApostrophes(s[i]);
      const base = stripDiacritics(ch2);
      // Each normalized base char points back to the original index i
      for (let j = 0; j < base.length; j++) {
        norm += base[j];
        map.push(i);
      }
    }
    return { norm, map };
  };

  const { norm: normSentence, map } = buildNormAndMap(sentence);

  // Case-insensitive search on the normalized sentence with whole-word boundaries
  const lowerNormSentence = normSentence.toLowerCase();
  const isWordChar = (ch) => /[a-z0-9]/i.test(ch);

  // Attempt to find the first match among the variants (prefer the first that matches)
  let matchStartNorm = -1;
  let matchNormLen = 0;

  for (const variant of uniqueVariants) {
    const normWord = normalizeForSearch(variant);
    if (!normWord) continue;

    let searchIndex = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const idx = lowerNormSentence.indexOf(normWord, searchIndex);
      if (idx === -1) break;

      const beforeChar = idx > 0 ? lowerNormSentence[idx - 1] : null;
      const afterChar =
        idx + normWord.length < lowerNormSentence.length
          ? lowerNormSentence[idx + normWord.length]
          : null;

      const endsWithApos = normWord.endsWith("'");
      // For Latin scripts, treat letters/digits as word chars.
      // For CJK, punctuation is not [a-z0-9], so boundaries are naturally respected.
      // Additionally, allow a following letter when the target ends with an apostrophe (e.g., "l' + enfant").
      const beforeBoundary = beforeChar == null || !isWordChar(beforeChar);
      const afterBoundary =
        afterChar == null || !isWordChar(afterChar) || endsWithApos;

      if (beforeBoundary && afterBoundary) {
        matchStartNorm = idx;
        matchNormLen = normWord.length;
        break;
      }

      searchIndex = idx + 1;
    }

    if (matchStartNorm !== -1) {
      break;
    }
  }

  if (matchStartNorm === -1) {
    // No whole-word match; return the sentence as-is
    return [sentence];
  }

  const endNorm = matchStartNorm + matchNormLen;

  // Map normalized indices back to original indices (end is exclusive)
  const startOrig = map[matchStartNorm] ?? 0;
  const endOrig = (map[endNorm - 1] ?? sentence.length - 1) + 1;

  const before = sentence.slice(0, startOrig);
  const after = sentence.slice(endOrig);

  // Return parts with a single blank
  return [before, BLANK_TOKEN, after];
}
